import { NavigateFunction } from 'react-router-dom';
import { captureEvent, resetAnalytics } from '../services/analytics';
import { flush as flushEventMirror } from '../services/eventMirror';
import { setMonitoringUser } from '../services/monitoring';

// Backend expiration timestamps are zone-less UTC ("2026-07-02T12:34:56").
// Treat them as UTC, tolerate values that already carry a zone, and fail
// CLOSED (expired) on anything unparseable — Safari rejects formats Chrome
// accepts (e.g. a space instead of "T"), and comparing against an Invalid
// Date would silently report the token as still valid.
export const isTokenExpired = (expirationDate: string | null): boolean => {
  if (!expirationDate) {
    return true;
  }
  const normalized = expirationDate.trim().replace(' ', 'T');
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
  const expireDate = new Date(hasZone ? normalized : normalized + 'Z');
  if (Number.isNaN(expireDate.getTime())) {
    return true;
  }
  return new Date() > expireDate;
};

// Role gates. Three functions rather than one because "holds an admin role"
// and "is an admin right now" are different questions, and impersonation makes
// them disagree.
//
// All three are functions and MUST be called. A bare `isActingAsAdmin` in a
// JSX guard is a function reference, which is always truthy, so the gate silently
// opens for everyone. tsc flags that as TS2774 in `fn && x` and `fn ? a : b`,
// but NOT in `!fn` — so read negated guards carefully.

// Pure: judges the role handed to it and reads no storage. Use it when you hold
// a role from an API response (`getMe().role`). Accepts undefined so an optional
// field (`role?: string` on the admin user DTOs) cannot be silently mistaken for
// "ask storage instead".
export const isPrivilegedRole = (role: string | null | undefined): boolean =>
  role === 'admin' || role === 'manager';

// The stored role of whoever signed in — NOT "this session is an admin".
// Admin.tsx handleImpersonate swaps accessToken/userId/userFirstName and sets
// impersonatedUsername, but deliberately leaves `userRole` untouched, so this
// stays true while an admin drives a plain user's account. That makes it the
// right check for exactly one thing: the escape hatches back to /admin, which
// must stay reachable mid-impersonation. Every other admin gate wants
// isActingAsAdmin() — using this one would leak the surface into an
// impersonated session, where the admin is supposed to see the user's app.
export const hasPrivilegedRole = (): boolean =>
  isPrivilegedRole(localStorage.getItem('userRole'));

// The default for admin-only surfaces: holds the role AND is not currently
// impersonating anyone.
export const isActingAsAdmin = (): boolean =>
  hasPrivilegedRole() && !localStorage.getItem('impersonatedUsername');

/**
 * Adopt a new session token. THE ONLY PLACE `accessToken` IS WRITTEN — sign-in,
 * email verification, password reset and admin impersonation all come through
 * here.
 *
 * It exists for the flush. The activity-log mirror batches for up to five
 * seconds and attributes what it sends to whoever the token names AT FLUSH
 * TIME, so writing a new token over a queue captured under the old one hands
 * one identity's events to another. That is not a race worth arguing about on
 * the impersonation path: the reload there is scheduled at 1000ms, well inside
 * the 5000ms window, so `pagehide` fires with the admin's browsing still
 * queued and posts it under the TARGET user's bearer — which the server tags
 * `impersonated_by`, so the rows look like ordinary impersonated activity
 * rather than obviously wrong data. `admin_impersonation_started` itself is in
 * that queue.
 *
 * Flushing, not dropping: `admin_impersonation_started` is the one event that
 * can only be captured at this instant and under the admin's own identity, and
 * the capture site is deliberately placed before this call to get that.
 *
 * `void` and not `await`: flush() reads the token and calls fetch()
 * synchronously before its first await, so the request is fully built by the
 * time the write below runs. Awaiting it would make every sign-in wait on an
 * analytics round trip.
 *
 * This is the twin of the flush in signOut() below, which handles the token
 * being REMOVED. Putting both in one place is the point: an identity change is
 * rare enough that a call site would forget, and the failure is silent.
 */
export const startSession = (accessToken: string, expiration: string): void => {
  void flushEventMirror();
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('accessTokenExpiration', expiration);
};

/**
 * End an impersonation and hand the session back to the admin who started it.
 *
 * The twin of `AdminUsers.handleImpersonate`, and the ONLY way out that is not
 * `signOut`. Before this existed there was none: `impersonatedUsername` was
 * written on the way in and removed only by signing out or by a 401, so an
 * admin who impersonated a user was stuck in that user's session for the rest
 * of the login.
 *
 * Returns false when the admin credentials needed to swap back are missing --
 * `adminAccessToken` is cleared by the 401 interceptor, so a session whose
 * admin token expired mid-impersonation lands here with nothing to restore.
 * The caller signs out instead; silently doing nothing would leave the button
 * looking broken.
 *
 * THE ORDER BELOW IS LOAD-BEARING, and it is not the mirror image of the start
 * path:
 *
 *  1. `startSession` FIRST. It flushes the activity-log queue before it writes,
 *     so everything the admin did while impersonating leaves under the TARGET's
 *     bearer -- which is what it was, and what makes the server stamp it
 *     `impersonated_by`. Removing `impersonatedUsername` before the flush would
 *     not change that stamp (the server reads the token, not our storage), but
 *     capturing the event before the flush WOULD: the stop is the admin's own
 *     act and would go out tagged as the impersonated user's.
 *  2. `impersonatedUsername` removed BEFORE the capture, because it gates the
 *     PostHog half of `captureEvent` -- `isImpersonating()` returns early -- so
 *     capturing above this line would record the stop in the activity log and
 *     nowhere else.
 *  3. The capture LAST, enqueued under the admin's own restored token. It rides
 *     out on the `pagehide` flush that the caller's navigation triggers.
 */
export const stopImpersonating = (): boolean => {
  const adminAccessToken = localStorage.getItem('adminAccessToken');
  const adminAccessTokenExpiration = localStorage.getItem('adminAccessTokenExpiration');
  if (!adminAccessToken || !adminAccessTokenExpiration) {
    return false;
  }

  startSession(adminAccessToken, adminAccessTokenExpiration);

  // Restore the admin's own identity over the target's. Both were stashed at
  // sign-in beside the token; a session that predates that change restores the
  // token and leaves these, which shows the admin the target's first name in
  // the header until the next navigation re-reads a `getMe`. Wrong-but-harmless
  // beats refusing to hand the session back.
  const adminUserId = localStorage.getItem('adminUserId');
  const adminUserFirstName = localStorage.getItem('adminUserFirstName');
  if (adminUserId) localStorage.setItem('userId', adminUserId);
  if (adminUserFirstName) localStorage.setItem('userFirstName', adminUserFirstName);

  localStorage.removeItem('impersonatedUsername');

  captureEvent('admin_impersonation_stopped');
  return true;
};

export const signOut = (navigate: NavigateFunction): void => {
  // Capture the logout (while identity is still set), then clear analytics state.
  captureEvent('user_signed_out');
  resetAnalytics();
  setMonitoringUser(null);

  // Send the queued activity-log events NOW, before the token they need is
  // removed below. flush() reads `accessToken` and calls fetch synchronously
  // before its first await, so the request is already built when the removal
  // runs on the next line — whereas the 5s batching timer would fire into a
  // signed-out browser, where flush() drops the queue rather than misattribute
  // it. `user_signed_out` itself is the event that would otherwise never
  // arrive, every single time.
  void flushEventMirror();

  // Delete items from local storage
  localStorage.removeItem('userFirstName');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('accessTokenExpiration');
  localStorage.removeItem('userId');
  localStorage.removeItem('userRole');
  localStorage.removeItem('adminAccessToken');
  localStorage.removeItem('adminAccessTokenExpiration');
  localStorage.removeItem('adminUserId');
  localStorage.removeItem('adminUserFirstName');
  localStorage.removeItem('impersonatedUsername');
  // Add more items to remove if needed

  // Navigate to the sign-in page
  navigate('/signin');
};